import { MouseEvent, useMemo, useState } from "react";
import {
  addRectToGroup,
  addTag,
  changeRectangleCategory,
  deleteRectangle,
  editRectangle,
  removeRectFromGroup,
  setRectangleCredence,
} from "@store/slices/workspaceSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectCategories,
  selectViewedGroups,
  selectViewedRectangles,
} from "@store/selectors";
import ContextMenu from "@shared/components/ContextMenu";
import TextAreaModal from "@shared/components/TextAreaModal";
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { collapsedDisplayBody, collapsedRectSize } from "@shared/utils/utils";
import { selectPageByAnnotationId } from "@store/selectors";
import { closeRectContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";

const RectangleContextMenu = () => {
  const [modalType, setModalType] = useState<"label" | "edit" | "">("");
  const [categoryAnchor, setCategoryAnchor] =
    useState<HTMLLIElement | null>(null);
  const [groupAnchor, setGroupAnchor] = useState<HTMLLIElement | null>(null);
  const dispatch = useAppDispatch();
  const rectangles = useAppSelector(selectViewedRectangles);
  const categories = useAppSelector(selectCategories);
  const groups = useAppSelector(selectViewedGroups);
  const pageByAnnotationId = useAppSelector(selectPageByAnnotationId);
  const { rectContextMenuIdx: idx, coords } = useAppSelector(
    (state) => state.canvasAux
  );

  const memberGroup = useMemo(
    () =>
      idx === null
        ? null
        : groups.find((g) => g.memberRectIndices.includes(idx)) ?? null,
    [groups, idx]
  );

  if (idx === null) return null;

  const rectangle = rectangles[idx];
  const currentCategory = rectangle?.category ?? "";
  const hasBadge =
    (rectangle?.source_page_number ??
      (rectangle?.source_annotation_client_id
        ? pageByAnnotationId[rectangle.source_annotation_client_id]
        : undefined)) !== undefined;

  const closeMenu = () => {
    dispatch(closeRectContextMenu());
    setCategoryAnchor(null);
    setGroupAnchor(null);
  };

  const handleDelete = () => {
    dispatch(deleteRectangle({ idx }));
    closeMenu();
  };

  const handleEditSubmit = (
    e: React.MouseEvent<HTMLButtonElement>,
    textInput: string
  ) => {
    e.preventDefault();
    dispatch(
      editRectangle({
        idx,
        textInput,
        // Size for what the collapsed node will actually display (the
        // tag, or a label derived from the new text) — clamped to the
        // label minimums so short text can't produce a tiny bubble.
        ...collapsedRectSize(
          collapsedDisplayBody(rectangle?.tag, textInput),
          hasBadge
        ),
      })
    );
    closeMenu();
    setModalType("");
  };

  const handleLabelSubmit = (
    e: React.MouseEvent<HTMLButtonElement>,
    textInput: string
  ) => {
    e.preventDefault();
    dispatch(addTag({ idx, textInput }));
    closeMenu();
    setModalType("");
  };

  const handleCancel = () => {
    closeMenu();
    setModalType("");
  };

  const handleCategoryClick = (event: MouseEvent<HTMLLIElement>) => {
    // Anchor the category submenu to the clicked "Change Category" item;
    // mirrors the SidebarContextMenu pattern for consistency between the
    // sidebar's highlight categorization and the canvas's rect categorization.
    setCategoryAnchor(event.currentTarget);
  };

  const handleCategorySelect = (category: string) => {
    dispatch(changeRectangleCategory({ idx, category }));
    closeMenu();
  };

  const handleGroupSubmenuOpen = (event: MouseEvent<HTMLLIElement>) => {
    setGroupAnchor(event.currentTarget);
  };
  const handleAddToGroup = (groupId: string) => {
    dispatch(addRectToGroup({ groupId, rectIdx: idx }));
    closeMenu();
  };
  const handleRemoveFromGroup = () => {
    dispatch(removeRectFromGroup({ rectIdx: idx }));
    closeMenu();
  };

  const handleCredenceChange = (_: Event, value: number | number[]) => {
    const credence = Array.isArray(value) ? (value[0] ?? 100) : value;
    dispatch(setRectangleCredence({ idx, credence }));
  };

  // Build group-related menu items conditionally so we only show the
  // "Add to group →" entry when there's somewhere to add to, and only
  // show "Remove from group" when the rect is currently grouped.
  const groupMenuButtons: import("@/types/types").ContextMenuButtons = [];
  if (memberGroup) {
    groupMenuButtons.push({
      text: `Remove from group${
        memberGroup.name ? ` (${memberGroup.name})` : ""
      }`,
      onClick: handleRemoveFromGroup,
    });
  } else if (groups.length > 0) {
    groupMenuButtons.push({
      text: "Add to group",
      onClick: handleGroupSubmenuOpen,
    });
  }

  const sharedMenuButtons = [
    { text: "Label", onClick: () => setModalType("label") },
    { text: "Change Category", onClick: handleCategoryClick },
    ...groupMenuButtons,
    { text: "Delete", onClick: handleDelete },
  ];

  const modals = {
    edit: {
      handleSubmit: handleEditSubmit,
      title: "Edit Text Content:",
      initialInput: rectangle?.text ?? "",
    },
    label: {
      handleSubmit: handleLabelSubmit,
      title: "Edit Label:",
      initialInput: rectangle?.tag ?? "",
    },
  };

  return (
    <>
      <ContextMenu
        open={true}
        menuCoords={coords}
        menuButtons={
          rectangle?.text
            ? [
                ...sharedMenuButtons,
                { text: "Edit", onClick: () => setModalType("edit") },
              ]
            : sharedMenuButtons
        }
        onClose={closeMenu}
      >
        <Divider />
        <MenuItem
          // Don't let slider events close the menu while dragging.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ flexDirection: "column", alignItems: "stretch" }}
          disableRipple
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              width: "100%",
            }}
          >
            <Typography variant="body1">Credence</Typography>
            <Typography variant="body1" color="text.secondary">
              {rectangle?.credence ?? 100}%
            </Typography>
          </Box>
          <Slider
            size="small"
            value={rectangle?.credence ?? 100}
            onChange={handleCredenceChange}
            min={1}
            max={100}
            step={1}
            sx={{ width: 180, mt: -0.5, alignSelf: "center" }}
            aria-label="Credence"
          />
        </MenuItem>
      </ContextMenu>
      <Menu
        anchorEl={categoryAnchor}
        open={Boolean(categoryAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <IconButton
          sx={{ position: "absolute", top: "0px", right: "0px" }}
          onClick={closeMenu}
        >
          <CloseIcon sx={{ fontSize: "13px" }} />
        </IconButton>
        <RadioGroup
          sx={{
            display: "flex",
            marginLeft: "5px",
            marginTop: "10px",
            maxWidth: "200px",
          }}
          value={currentCategory}
        >
          {categories.map(({ label, background }) => (
            <FormControlLabel
              key={label}
              value={label}
              control={
                <Radio
                  value={label}
                  sx={{ color: background }}
                  inputProps={{ "aria-label": "controlled" }}
                  onChange={() => handleCategorySelect(label)}
                />
              }
              label={label}
            />
          ))}
        </RadioGroup>
      </Menu>
      <Menu
        anchorEl={groupAnchor}
        open={Boolean(groupAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {groups.map((g) => (
          <MenuItem key={g.id} onClick={() => handleAddToGroup(g.id)}>
            <ListItemText
              primary={g.name?.trim() || "Group"}
              secondary={`${g.memberRectIndices.length} member${
                g.memberRectIndices.length === 1 ? "" : "s"
              }`}
            />
          </MenuItem>
        ))}
      </Menu>
      {modalType ? (
        <TextAreaModal handleCancel={handleCancel} {...modals[modalType]} />
      ) : null}
    </>
  );
};

export default RectangleContextMenu;
